import { AssetPriceMap } from 'app/queries/price/useQueryAssetPrice';
import { constUndefined, flow, pipe } from 'fp-ts/lib/function';
import * as Monoid from 'fp-ts/Monoid';
import * as Array from 'fp-ts/Array';
import * as Option from 'fp-ts/Option';
import * as Apply from 'fp-ts/lib/Apply';
import { LTV } from '../../../../util/ltv';
import { BigIntWithDecimals, ChecksumAddress, LiquidationPriceLegacy } from '@silo/types';
import { option } from 'fp-ts';
import { BigIntWithDecimalsSum, convertCurrency } from '@silo/utils';
import { PositionAsset, Positions } from '@silo/ui';

interface PositionsTotals {
  totalDepositedUSD: BigIntWithDecimals;
  totalBorrowedUSD: BigIntWithDecimals;
}

const totalsMonoid = Monoid.struct<PositionsTotals>({
  totalDepositedUSD: BigIntWithDecimalsSum,
  totalBorrowedUSD: BigIntWithDecimalsSum,
});

// Positions utilities
type PositionsUtility<T> = (positions: Positions) => T;

const getPositionsTotals = (assetPriceMap: AssetPriceMap): PositionsUtility<PositionsTotals | undefined> =>
  flow(({ assets }) => assets, Array.map(getPositionAssetTotals(assetPriceMap)), aggregatePositionsTotals);

// PositionAsset utilities
type PositionAssetUtility<T> = (asset: PositionAsset) => T;

const getEffectiveCollateral: PositionAssetUtility<bigint> = ({ collateralBalance, collateralOnlyBalance }) =>
  collateralBalance + collateralOnlyBalance;

const getPositionAssetTotals =
  (assetPriceMap: AssetPriceMap): PositionAssetUtility<PositionsTotals | undefined> =>
  (asset) => {
    const { assetAddress, priceInUSD: initialPriceInUSD } = asset;
    const priceInUSD = assetPriceMap.get(assetAddress) ?? initialPriceInUSD;

    if (!priceInUSD) {
      return undefined;
    }

    const { decimals, debtBalance } = asset;
    const effectiveCollateral = getEffectiveCollateral(asset);
    const totalDepositedUSD = convertCurrency({ value: effectiveCollateral, decimals }, priceInUSD);
    const totalBorrowedUSD = convertCurrency({ value: debtBalance, decimals }, priceInUSD);

    return { totalDepositedUSD, totalBorrowedUSD };
  };

// Positions[] utilities
export const getPositionsArrayTotals = (assetPriceMap: AssetPriceMap) => (positionsArray: Positions[]) =>
  pipe(positionsArray, Array.map(getPositionsTotals(assetPriceMap)), aggregatePositionsTotals);

// misc utilities
const aggregatePositionsTotals = (positionsTotalsArray: (PositionsTotals | undefined)[]) =>
  pipe(
    positionsTotalsArray,
    Array.map(Option.fromNullable),
    ([totals, ...array]) => Apply.sequenceT(Option.Apply)(totals ?? Option.some(totalsMonoid.empty), ...array),
    Option.map(Monoid.concatAll(totalsMonoid)),
    Option.getOrElseW(constUndefined)
  );

export const getLiquidationPriceFromLtv = (ltv: LTV | null, assetAddress: ChecksumAddress): LiquidationPriceLegacy =>
  pipe(
    ltv,
    option.fromNullable,
    option.map(({ collateralAssetsMap, debtAssetsMap }) => ({ ...collateralAssetsMap, ...debtAssetsMap })),
    option.map((assetsMap) => assetsMap[assetAddress]),
    option.chain(option.fromNullable),
    option.map((metrics) => metrics.liquidationPrice),
    option.getOrElse((): LiquidationPriceLegacy => ({ _tag: 'notApplicable' }))
  );
